import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from './Dashboard';
import { Gift, ArrowRight, Check, Sparkle, Lock, Receipt } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PACK_LABEL = { starter: 'Starter', pro: 'Pro Pack', power: 'Power', ultimate: 'Ultimate' };

export default function RedeemGift() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get('code') || '');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(null);

  const previewCode = useCallback(async (raw) => {
    if (!raw || raw.replace(/[^a-z0-9]/gi, '').length < 4) { setPreview(null); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/credits/gifts/lookup`, {
        params: { code: raw },
      });
      setPreview(data);
    } catch (err) {
      setPreview({ error: err.response?.data?.detail || t('gift.err_not_found') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const t = setTimeout(() => previewCode(code), 400);
    return () => clearTimeout(t);
  }, [code, previewCode]);

  const handleRedeem = async () => {
    setRedeeming(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/credits/redeem`, { code });
      setRedeemed(data);
      toast.success(t('gift.success_toast', { n: data.credited }));
      setTimeout(() => navigate('/credits'), 2200);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 401) {
        toast.error(t('gift.login_required'));
        navigate('/?return_to=' + encodeURIComponent(`/credits/redeem?code=${code}`));
        return;
      }
      toast.error(detail || t('gift.err_redeem'));
    } finally {
      setRedeeming(false);
    }
  };

  const isRedeemable = preview && !preview.error && preview.status === 'issued';
  const isAlreadyRedeemed = preview && preview.status === 'redeemed';

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F59E0B]/15 border border-[#F59E0B]/30 rounded-full mb-3">
            <Gift size={14} weight="fill" className="text-[#F59E0B]" />
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#F59E0B] font-semibold">{t('gift.kicker')}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-[Outfit] font-bold tracking-tight">{t('gift.title')}</h1>
          <p className="text-white/60 text-sm mt-2 max-w-md mx-auto">{t('gift.sub')}</p>
        </div>

        <div className="p-6 bg-[#121212] border border-white/10 rounded-2xl space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-white/50">{t('gift.code_label')}</label>
            <input data-testid="input-gift-code" value={code} onChange={(e) => setCode(e.target.value)}
              placeholder="XXXX-XXXX-XXXX" autoFocus
              className="w-full mt-1 px-4 py-3 bg-[#0A0A0A] border border-white/15 rounded-xl text-lg font-mono tracking-widest text-center focus:outline-none focus:border-[#F59E0B] uppercase" />
          </div>

          {loading && (
            <div className="text-center text-xs text-white/40 py-2">{t('gift.checking')}</div>
          )}

          {preview && preview.error && (
            <div className="p-3 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg text-sm text-[#EF4444]" data-testid="gift-preview-error">
              {preview.error}
            </div>
          )}

          {preview && !preview.error && (
            <div className="p-4 bg-gradient-to-br from-[#F59E0B]/12 to-[#A855F7]/10 border border-[#F59E0B]/30 rounded-xl space-y-2" data-testid="gift-preview">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#F59E0B] font-bold">{t('gift.youll_receive')}</div>
                  <div className="text-3xl font-[Outfit] font-bold text-white">
                    {preview.credits} <span className="text-base text-white/55 font-normal">{t('gift.credits')}</span>
                  </div>
                  <div className="text-xs text-white/55 mt-1">Pack {PACK_LABEL[preview.pack_id] || preview.pack_id || '—'}</div>
                </div>
                {isRedeemable && (
                  <div className="px-2.5 py-1 rounded bg-[#10B981]/15 text-[#10B981] text-[10px] uppercase tracking-wider font-bold flex items-center gap-1">
                    <Check size={10} weight="bold" /> {t('gift.valid')}
                  </div>
                )}
                {isAlreadyRedeemed && (
                  <div className="px-2.5 py-1 rounded bg-[#EF4444]/15 text-[#EF4444] text-[10px] uppercase tracking-wider font-bold">
                    {t('gift.already_redeemed')}
                  </div>
                )}
              </div>
              {preview.gifter_name && (
                <div className="text-sm text-white/75"><span className="text-white/45">{t('gift.from')}</span> <strong>{preview.gifter_name}</strong></div>
              )}
              {preview.message && (
                <div className="p-3 bg-white/5 border-l-2 border-[#A855F7] rounded-r text-sm italic text-white/80">
                  &ldquo;{preview.message}&rdquo;
                </div>
              )}
            </div>
          )}

          {redeemed ? (
            <div className="p-4 bg-[#10B981]/15 border border-[#10B981]/40 rounded-xl text-center" data-testid="redeem-success">
              <Sparkle size={32} weight="bold" className="text-[#10B981] mx-auto mb-2" />
              <div className="font-[Outfit] font-bold text-lg">{t('gift.success_title', { n: redeemed.credited })}</div>
              <div className="text-xs text-white/60 mt-1">{t('gift.redirecting')}</div>
            </div>
          ) : (
            <button data-testid="btn-redeem" onClick={handleRedeem}
              disabled={!isRedeemable || redeeming}
              className="w-full px-4 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg,#F59E0B,#A855F7)', color: '#fff' }}>
              {redeeming ? t('gift.redeeming') : <>{isAlreadyRedeemed ? <><Lock size={14} weight="bold" /> {t('gift.was_redeemed')}</> : <>{preview && !preview.error ? t('gift.redeem_amount', { n: preview.credits }) : t('gift.redeem_plain')} <ArrowRight size={14} weight="bold" /></>}</>}
            </button>
          )}
        </div>

        <div className="text-center text-xs text-white/40">
          <p>{t('gift.footer')}</p>
        </div>
      </div>
    </DashboardLayout>
  );
}
